import "twin.macro";

import { useState } from "react";
import { useNavigate } from "react-router";

import { Archive, CloseRounded } from "@mui/icons-material";
import { Button, Chip, Collapse, IconButton, Tooltip } from "@mui/material";

import {
  Contained,
  FixedHeightScrollLastChild,
  OpaqueCard,
  PageTitle,
} from "@components/StyledComponents";
import useConfirm from "@features/confirm";
import { DemandPlanForm } from "@features/demandPlanning/DemandPlanForm";
import {
  useDemandPlanListQuery,
  useUpdateDemandPlanMutation,
} from "@features/demandPlanning/data/demandPlanQueries";
import Filters, { useFilterParams } from "@features/filters";
import IndexTable from "@features/ui/indexTable";
import { DemandPlan } from "@models/DemandPlan";
import DocTitle from "@utility/DocTitle";

const DemandPlans = () => {
  const [filters] = useFilterParams();
  const noFilters = !Object.keys(filters).length;

  const {
    data = [],
    error,
    isLoading,
  } = useDemandPlanListQuery({
    filter: {
      searchTerm: filters.q,
    },
  });
  const noPlansInSystem = !isLoading && data.length === 0 && noFilters;

  const [isCreating, setCreating] = useState(false);

  const navigate = useNavigate();

  const navigateToDemandPlan = (plan: DemandPlan): void => {
    navigate(`/purchasing/demand-planning/${plan.id}`);
  };

  const handleCloseAdd = () => {
    setCreating(false);
  };

  const confirm = useConfirm();
  const updateMutation = useUpdateDemandPlanMutation();

  return (
    <>
      <DocTitle title={"Demand Planning"} />
      <div tw="flex max-w-full">
        <FixedHeightScrollLastChild tw="flex-1 min-w-0">
          <div tw="flex justify-between items-center flex-wrap w-full">
            <h2 tw="text-2xl text-neutral-600 font-bold mb-2">
              Demand Planning
            </h2>
            <Contained tw="flex justify-end gap-2 absolute right-0">
              <Button
                variant="contained"
                onClick={() => {
                  setCreating(true);
                }}
              >
                New Demand Plan
              </Button>
            </Contained>
          </div>
          <Filters searchTitle="Search..." slots={[]} />
          <OpaqueCard tw="p-0 overflow-hidden relative">
            {noPlansInSystem && (
              <div tw="place-content-center flex">
                <div tw="py-24 text-gray-600">
                  No demand plans found, create a plan to start demand planning!
                </div>
              </div>
            )}
            {!noPlansInSystem && (
              <IndexTable
                isLoading={isLoading}
                onRowClick={(row) => navigateToDemandPlan(row)}
                rows={data}
                error={error}
                columns={[
                  {
                    id: "delete",
                    label: "",
                    className:
                      "content-center pb-0 pt-[6px] pr-0 pl-2 w-[40px]",
                    render: (_v, row) => {
                      return (
                        <Tooltip title="Archive Demand Plan">
                          <IconButton
                            onClick={async (e) => {
                              e.stopPropagation();

                              const goArchive = await confirm(
                                `This will archive demand plan: ${row.name}`
                              );

                              if (goArchive) {
                                updateMutation.mutate({
                                  id: row.id,
                                  isActive: false,
                                });
                              }
                            }}
                          >
                            <Archive />
                          </IconButton>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    id: "name",
                    label: "Name",
                    className: "min-w-[300px] content-center",
                  },
                  {
                    id: "orderWindows",
                    label: "Order Windows",
                    render: (windows) =>
                      windows.map((w) => <Chip tw="m-0.5" label={w.name} />),
                  },
                ]}
              />
            )}
          </OpaqueCard>
        </FixedHeightScrollLastChild>
        <Collapse
          in={isCreating}
          timeout={300}
          unmountOnExit
          orientation="horizontal"
          tw="z-20 shrink-0 -mt-4 [--space-after-header: -16px]"
          className="body-height-after-header"
        >
          <div tw="w-[480px] h-full bg-white relative px-6 pt-6 pb-3">
            <div tw="flex justify-between items-center">
              <PageTitle>New Demand Plan</PageTitle>
              <Tooltip title="Close" placement="top">
                <IconButton onClick={handleCloseAdd} edge="end">
                  <CloseRounded />
                </IconButton>
              </Tooltip>
            </div>
            <DemandPlanForm onSaveSuccess={(dp) => navigateToDemandPlan(dp)} />
          </div>
        </Collapse>
      </div>
    </>
  );
};

export default DemandPlans;
